import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import articuloXListaPrecioService from '../../services/articuloXListaPrecioService';
import * as XLSX from 'xlsx';

interface ArticuloXListaPrecioPagedResult {
  items: any[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const ArticuloXListaPrecio: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [articulosData, setArticulosData] = useState<ArticuloXListaPrecioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchArticulos = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await articuloXListaPrecioService.getArticuloXListaPrecios({
        pageNumber,
        pageSize,
        searchTerm: search || undefined
      });
      setArticulosData(response);
    } catch (error: any) {
      console.error('Error al cargar artículos por lista:', error);
      enqueueSnackbar('Error al cargar artículos por lista', { variant: 'error' });
      setArticulosData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchArticulos(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (codigoLista: string, codigoArticulo: string) => {
    if (window.confirm('¿Está seguro de eliminar este artículo de la lista?')) {
      try {
        await articuloXListaPrecioService.deleteArticuloXListaPrecio(codigoLista, codigoArticulo);
        enqueueSnackbar('Artículo eliminado exitosamente', { variant: 'success' });
        fetchArticulos(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar artículo:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar artículo', { variant: 'error' });
      }
    }
  };

  const handleExportExcel = () => {
    const dataToExport = articulosData.items.map(a => ({
      'Lista': a.codigoLista,
      'Artículo': a.codigoArticulo,
      'Descripción': a.descripcion,
      'Precio Venta': a.precioVta,
      'Precio Costo': a.precioCosto,
      'Ramo': a.nombreRamo,
      'Línea': a.nombreLinea,
      'Clase': a.nombreClase
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Artículos');
    XLSX.writeFile(wb, 'Articulos_Lista_Precio.xlsx');
    enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as any[]) {
          try {
            const articulo = {
              codigoLista: row['Lista'] || row['codigoLista'],
              codigoArticulo: row['Artículo'] || row['codigoArticulo'],
              descripcion: row['Descripción'] || row['descripcion'],
              precioVta: Number(row['Precio Venta'] || row['precioVta'] || 0),
              precioCosto: Number(row['Precio Costo'] || row['precioCosto'] || 0),
              propio: row['propio'] || 'N',
              iva: row['iva'] || 'S',
              fleteFp: row['fleteFp'] || 'N',
              codigoRamo: row['codigoRamo'] || '',
              nombreRamo: row['Ramo'] || row['nombreRamo'] || '',
              codigoLinea: row['codigoLinea'] || '',
              nombreLinea: row['Línea'] || row['nombreLinea'] || '',
              codigoClase: row['codigoClase'] || '',
              nombreClase: row['Clase'] || row['nombreClase'] || ''
            };

            await articuloXListaPrecioService.createArticuloXListaPrecio(articulo);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar artículo:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, { 
          variant: errorCount > 0 ? 'warning' : 'success' 
        });
        fetchArticulos(page + 1, rowsPerPage, searchTerm);
      } catch (error) {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Artículos por Lista de Precio
            </Typography>
            <Stack direction="row" spacing={1}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-articulos"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-articulos">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
                Exportar
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/ventas/articulo-lista-precio/nuevo')}>
                Nuevo Artículo
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {articulosData.items.map((articulo) => (
                <Card key={`${articulo.codigoLista}-${articulo.codigoArticulo}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {articulo.codigoArticulo} - {articulo.descripcion}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lista: {articulo.codigoLista}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Precio: ${articulo.precioVta}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/ventas/articulo-lista-precio/${articulo.codigoLista}/${articulo.codigoArticulo}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(articulo.codigoLista, articulo.codigoArticulo)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Lista</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Precio Venta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Precio Costo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articulosData.items.map((articulo) => (
                    <TableRow key={`${articulo.codigoLista}-${articulo.codigoArticulo}`} hover>
                      <TableCell>{articulo.codigoLista}</TableCell>
                      <TableCell>{articulo.codigoArticulo}</TableCell>
                      <TableCell>{articulo.descripcion}</TableCell>
                      <TableCell>${articulo.precioVta?.toFixed(2)}</TableCell>
                      <TableCell>${articulo.precioCosto?.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/ventas/articulo-lista-precio/${articulo.codigoLista}/${articulo.codigoArticulo}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(articulo.codigoLista, articulo.codigoArticulo)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={articulosData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ArticuloXListaPrecio;
